import { Alert, Box, Button, CircularProgress, DialogActions, IconButton, Link, Stack, Tooltip, Typography } from "@mui/material"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import PaperComponent from "components/common/paper-component"
import { useCSVReader } from "react-papaparse"
import { useState, useCallback, useRef, useEffect } from "react"
import { UploadZone, SpaceBetweenBoxWrapper, DialogContentWrapper, UploadZoneWrapper, TypographyAlignRight } from "./styles"
import Upload from "assets/upload.png"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import CloseIcon from "@mui/icons-material/Close"
import PublishIcon from "@mui/icons-material/Publish"
import * as XLSX from "xlsx"
import PapaParse from "papaparse"
import { DataGrid } from "@mui/x-data-grid"
import InfoIcon from "@mui/icons-material/Info"
import { formatDisplayDate } from "utils/date-helper"
import { rootStore, useStores } from "stores/root-store"
import ImportConfirmDialog from "./import-confirm-dialog"
import DisplayResultDialog from "./display-result-dialog"
import { ProgressWrapper } from "components/common-styles"
import { errorType, trackEvent } from "utils/analytics"
import { storageName } from "shared/constants/storage-constant"
import * as StorageHelper from "utils/storage-helper"
import { v4 as uuidv4 } from "uuid"
import { CheckCircle, DoneAll, ErrorOutline } from "@mui/icons-material"
import * as DBHelper from "utils/db-helper"

// Upload dialog titles -> analytics entity names
const UPLOAD_ENTITY = { "sales order": "sales_order", "inbound delivery": "inbound_delivery", inventory: "product" }

const UploadDialog = ({ moduleName, columns, columnsUploadFunctionsMap, startFrom, title, filteType, downloadLink, open, onClose, DataStore }) => {
  const { FileUploadHistoryStore } = useStores()
  const { CSVReader } = useCSVReader()
  const [zoneHover, setZoneHover] = useState(false)
  const [data, setData] = useState(null)
  const [validation, setValidation] = useState({})
  const [message, setMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [openAlert, setOpenAlert] = useState(false)
  const [alertContent, setAlertContent] = useState("")
  const [wasSuccess, setWasSuccess] = useState(true)
  const [openConfirm, setOpenConfirm] = useState(false)
  const [isDiscard, setIsDiscard] = useState(false)
  const [showErrorOnly, setShowErrorOnly] = useState(false)
  const [invalidItems, setInvalidItems] = useState({})
  const [displayColumns, setDisplayColumns] = useState([])

  const curEditingField = useRef("")
  const curFileName = useRef("")
  const filedRemoved = useRef(false)
  const curFileContent = useRef("")

  const onUploadAccepted = async (results, file) => {
    setZoneHover(false)
    filedRemoved.current = false

    const fileName = file.name.split(".")
    const fileExtension = fileName[file.name.split(".").length - 1]
    curFileName.current = file.name

    if (fileExtension.indexOf("xls") >= 0) {
      const reader = new window.FileReader()
      reader.readAsBinaryString(file)
      reader.onload = e => {
        const wb = XLSX.read(e.target.result, { type: "binary" })
        const wsname = wb.SheetNames[0]
        const ws = wb.Sheets[wsname]
        const csv = XLSX.utils.sheet_to_csv(ws)
        const tmp = []
        PapaParse.parse(csv, {
          complete: async () => {
            await afterReadingData(tmp)
          },
          step: row => {
            tmp.push(row.data)
          },
          skipEmptyLines: true,
        })

        // Encode the content to Base64
        curFileContent.current = btoa(e.target.result)
      }
    } else {
      const fileReader = new FileReader()
      fileReader.readAsText(file)
      fileReader.onload = function () {
        // Encode the content to Base64
        curFileContent.current = btoa(fileReader.result)
      }

      await afterReadingData(results.data, true)
    }

    //onAfterUploadingData("OK")
  }

  const afterReadingData = async (rows, isCSV = false) => {
    const rowData = []

    const alphabet = "abcdefghijklmnopqrstuvwxyz".toLocaleUpperCase().split("")
    if (startFrom == null || isCSV) startFrom = 0
    const newHeaders = rows.length > startFrom && rows[startFrom] ? rows[startFrom] : []
    let msg = ""

    for (let i = 0; i < columns.length; i++) {
      const headerName = columns[i].headerName
      const mappings = columns[i].mappings
      const found = newHeaders.find(x => headerName == x || mappings?.includes(x))
      if (!found) {
        msg = `Failed to upload because missing column <b>"${headerName}"</b>`

        break
      }
    }

    if (!msg) {
      let id = 1
      // ignore the first row (header texts)
      for (let i = startFrom + 1; i < rows.length; i++) {
        if (rows[i].length < columns.length) continue

        let shouldSkipLine = true
        const row = { id }
        for (let j = 0; j < newHeaders.length; j++) {
          const headerName = newHeaders[j]
          const col = columns.find(x => headerName == x.headerName || x.mappings?.includes(headerName))
          if (!col) continue

          const val = (rows[i][j] || "").trim()
          row[col.field] = val

          if (val.length > 0) shouldSkipLine = false
        }

        if (!shouldSkipLine) {
          rowData.push(row)
          id++
        }
      }

      if (rowData.length == 0) msg = "You can not upload empty file."
    }

    setMessage(msg)

    if (!msg) {
      // validate cell data
      const newValidation = validateAllRows(rowData)

      for (let i = 0; i < rowData.length; i++) {
        if (filedRemoved.current) return

        const row = rowData[i]
        for (let j = 0; j < columns.length; j++) {
          const col = columns[j]
          if (col.serverSideValidation && !col.shouldNotServerSideValidationFirstTime) {
            newValidation[row.id][col.field] = await col.serverSideValidation(row.id, row[col.field], rowData)
          }
        }
      }
      setValidation(newValidation)

      // update states
      setData(rowData)

      StorageHelper.setStorage(storageName.importingModule, moduleName)

      DBHelper.addData({
        id: 1,
        data: rowData,
        columns: displayColumns,
        invalidItems: invalidItems,
        validation: newValidation,
        curFileName: curFileName.current,
        curFileContent: curFileContent.current,
      })
    }
  }

  const validateAllRows = (rowData, onlyForField = "") => {
    const newValidation = { ...validation }
    ;(rowData || []).forEach(row => {
      columns.forEach(col => {
        if (!onlyForField || col.field == onlyForField) {
          if (!newValidation[row.id]) {
            newValidation[row.id] = {}
          }
          const item = newValidation[row.id]
          item[col.field] = validateColumnData(row.id, row[col.field], col, rowData)
        }
      })
    })

    if (!onlyForField) setValidation(newValidation)

    return newValidation
  }

  const resetScreen = () => {
    setMessage("")
    setData(null)
    setZoneHover(false)
    setIsDiscard(false)
    setInvalidItems({})
    setWasSuccess(true)
  }

  useEffect(() => {
    if (open) {
      resetScreen()

      const importingModule = StorageHelper.getStorage(storageName.importingModule)
      if (importingModule) {
        DBHelper.getData().then(res => {
          if (res) {
            setDisplayColumns(res.columns)
            setData(res.data)
            setInvalidItems(res.invalidItems)
            setValidation(res.validation)

            curFileName.current = res.curFileName
            curFileContent.current = res.curFileContent
          } else {
            updateDisplayColumns()
          }
        })
      } else {
        updateDisplayColumns()
      }
    }
  }, [open])

  const handleClose = (ev, reason) => {
    if (reason === "backdropClick" || isSubmitting) return

    StorageHelper.deleteStorage(storageName.importingModule)
    DBHelper.clearData()

    if (onClose) onClose()
  }

  const handleDiscard = () => {
    setIsDiscard(true)
    setOpenConfirm(true)
  }

  const handleSubmit = () => {
    setIsDiscard(false)
    setOpenConfirm(true)
  }

  const isDataValid = () => {
    let isValid = true
    for (const prop in validation) {
      for (const item in validation[prop]) {
        if (validation[prop][item]) {
          isValid = false
          break
        }
      }
      if (!isValid) break
    }

    return isValid
  }

  const validateColumnData = (rowId, val, col, allData) => {
    let errorMessage = col.required && !val ? "Missing Value" : ""
    if (col.type == "number" && !errorMessage && isNaN(val)) errorMessage = "Invalid value"
    if (!errorMessage) errorMessage = col.maxLength != null && val?.length > col.maxLength ? `Max Length is ${col.maxLength} characters` : ""
    if (!errorMessage) errorMessage = col.minValue != null && parseFloat(val) < col.minValue ? `Value must be greater than ${col.minValue}` : ""
    if (!errorMessage) errorMessage = col.maxValue != null && parseFloat(val) > col.maxValue ? `Value must be less than ${col.maxValue}` : ""
    if (!errorMessage && val && columnsUploadFunctionsMap && columnsUploadFunctionsMap[col.field] && columnsUploadFunctionsMap[col.field].customValidation)
      errorMessage = columnsUploadFunctionsMap[col.field].customValidation(rowId, val, allData || data)

    return errorMessage
  }

  const preProcessEditCellProps = async (params, col) => {
    const val = (params.props.value || "").toString().trim()
    const newValidation = { ...validation }

    if (!newValidation[params.id]) {
      newValidation[params.id] = {}
    }
    const item = newValidation[params.id]
    item[col.field] = validateColumnData(params.id, val, col)

    setValidation(newValidation)

    curEditingField.current = col.field

    return { ...params.props }
  }

  const renderCell = params => {
    if (params.field == "result") {
      return params.value ? (
        <Tooltip placement="top" title={params.value}>
          <InfoIcon color="error" />
        </Tooltip>
      ) : (
        <CheckCircle color="success" />
      )
    }

    const itemValidation = validation[params.id]
    const errorMessage = itemValidation && itemValidation[params.field]

    let val = params.value && params.value instanceof Date ? formatDisplayDate(params.value) : params.value?.toString()
    if (params.field.includes("country")) val = rootStore.getTextByValue("COUNTRY", params.value)

    return (
      <SpaceBetweenBoxWrapper>
        {params.colDef.type == "number" ? <TypographyAlignRight noWrap>{val}</TypographyAlignRight> : <Typography noWrap>{val}</Typography>}
        {errorMessage && (
          <Tooltip placement="top" title={errorMessage}>
            <InfoIcon color="error" />
          </Tooltip>
        )}
      </SpaceBetweenBoxWrapper>
    )
  }

  const processRowUpdate = async newRow => {
    const newValidation = { ...validation }
    const newData = data.map(row => (row.id === newRow.id ? newRow : row))
    if (curEditingField.current) {
      const col = columns.find(x => x.field == curEditingField.current)
      if (col && col.shouldValidateAllRowsAfterChanging) validateAllRows(newData, curEditingField.current)

      if (col && col.serverSideValidation) {
        if (!newValidation[newRow.id]) {
          newValidation[newRow.id] = {}
        }

        if (!newValidation[newRow.id][curEditingField.current]) {
          newValidation[newRow.id][curEditingField.current] = await col.serverSideValidation(newRow.id, newRow[curEditingField.current], data)

          setValidation(newValidation)
        }
      }
    }
    setData(newData)

    DBHelper.addData({
      id: 1,
      data: newData,
      columns: displayColumns,
      invalidItems: invalidItems,
      validation: newValidation,
      curFileName: curFileName.current,
      curFileContent: curFileContent.current,
    })

    return newRow
  }

  const handleProcessRowUpdateError = useCallback(error => {
    //console.log(error)
  }, [])

  const getErrorMessage = (x, invalidData) => {
    if (moduleName == "SaleOrder") return invalidData[`${StorageHelper.getStorage(storageName.activeCompany)}-${x["erp_order"].toUpperCase()}`]
    else if (moduleName == "ASN") return invalidData[`${x["receipt_id"].toUpperCase()}`]
    return invalidData[`${x["item"].toUpperCase()}`]
  }

  const handleConfirmOrDiscardImport = async () => {
    if (isDiscard) {
      handleClose()
      return
    }

    setIsSubmitting(true)
    if (DataStore) {
      const invalidData = {}
      let success = true
      const rows = getDataForUpload()
      const result = await DataStore.uploadData(rows)
      /*const result = {
        rejectedTransactions: 6,
        message: `SHIPMENT XML Download ended. : Invalid shipment (ESL-R-SAIDTALHADEMIR):Invalid ship to address.:Invalid state "Keçioren". - Invalid shipment (ESL-R-IANPARTRIDGE):Invalid ship to address.:Invalid state "Paphos". - Invalid shipment (ESL-R-SIMAFEIYUSHKIS):Invalid ship to address.:Invalid state "Vilnius m.". - Invalid shipment (ESL-R-OLIVÉRHARANGI):Invalid ship to address.:Invalid state "Hajdu-Bihar County". - Invalid shipment (ESL-R-EZHELANDOVSKAYA):Invalid ship to address.:Invalid state "Limassol". - Invalid shipment (ESL-R-MARSELKHAIDAROV):Invalid ship to address.:Invalid state "Limassol". -`,
      }*/
      let msg = "New Data Added Successfully"
      const rejectedTransactions = result?.rejectedTransactions || result?.rejected_transactions || result?.resource?.rejectedTransactions || result?.error?.context?.rejectedTransactions || 0
      if (
        result &&
        (result.resource?.message || result.error?.message || result.message || typeof result == "string") &&
        (rejectedTransactions > 0 || (result.indexOf && result.indexOf("Invalid") >= 0))
      ) {
        success = false
        msg = result.resource?.message || result.error?.message || result.message || result

        const regex = /Invalid\s+(shipment|receipt|item)\s*\(([^)]+)\):\s*([\s\S]*?)(?=\s*-\s*(?:Invalid|$))/g

        for (const match of msg.matchAll(regex)) {
          const [, type, id, message] = match
          invalidData[id] = message?.trim()
        }

        const newData = data.map(x => {
          return { result: getErrorMessage(x, invalidData), ...x }
        })
        setData(newData)
      }

      trackEvent("file_upload", {
        entity: UPLOAD_ENTITY[String(title).toLowerCase()] || String(title).toLowerCase().replace(/\W+/g, "_"),
        rows: rows.length,
        result: success ? "success" : "failed",
        error_type: success ? undefined : errorType(result),
      })
      setWasSuccess(success)
      updateDisplayColumns(!success)
      setInvalidItems(invalidData)
      setAlertContent(msg)
      setOpenAlert(true)

      onAfterUploadingData(msg)
    }
    setIsSubmitting(false)
  }

  const getDataForUpload = () => {
    return wasSuccess ? data : data.filter(x => Boolean(getErrorMessage(x, invalidItems)))
  }

  const updateDisplayColumns = (includeResultColumn = false) => {
    const newColumns = columns.map(x => {
      return {
        ...x,
      }
    })

    if (includeResultColumn) {
      setDisplayColumns([
        {
          field: "result",
          headerName: "",
          width: 40,
          editable: false,
        },
        ...newColumns,
      ])
    } else {
      setDisplayColumns(newColumns)
    }
  }

  const onAfterUploadingData = async result => {
    /*const res = FileUploadHistoryStore.addData({
      ID: uuidv4(),
      UploadType: moduleName,
      UserName: rootStore.userEmail,
      DateTimeStamp: new Date(),
      FileName: curFileName.current,
      FileContent: curFileContent.current,
      UploadResult: result,
    })
    console.log(res)*/
  }

  const handleConfirmInDisplayResult = async () => {
    setOpenAlert(false)
    if (wasSuccess) {
      resetScreen()
    }
  }

  const handleCloseInDisplayResult = () => {
    setOpenAlert(false)
    handleClose()
  }

  const filteredData = showErrorOnly
    ? data?.filter(x => {
        const item = validation[x.id]
        const msg = getErrorMessage(x, invalidItems)
        return (item && Object.keys(item).some(key => item[key])) || Boolean(msg)
      })
    : data

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullScreen fullWidth={true} maxWidth={"xl"} PaperComponent={PaperComponent} aria-labelledby="draggable-dialog-title" disableEscapeKeyDown={true}>
        <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
          <h2>
            Import {title} by {filteType ? filteType : "csv/xls/xlsx"}
          </h2>
          <h6>
            Automatically create new {title} by uploading {filteType ? filteType : "csv/xls/xlsx"} file that follow our standards
          </h6>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContentWrapper dividers>
          {data && !message && !filedRemoved.current ? (
            <DataGrid
              getRowId={row => row.id}
              rows={filteredData}
              columns={displayColumns.map(x => {
                const functionsMap = columnsUploadFunctionsMap ? columnsUploadFunctionsMap[x.field] : {}
                return {
                  ...x,
                  ...functionsMap,
                  renderCell,
                  preProcessEditCellProps: async params => await preProcessEditCellProps(params, x),
                }
              })}
              editMode="cell"
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              disableRowSelectionOnClick
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 100,
                  },
                },
              }}
              pageSizeOptions={[50, 100, 200]}
              onCellEditStart={(params, event) => {
                if (!wasSuccess && !params.row.result) {
                  event.defaultMuiPrevented = true
                }
              }}
            />
          ) : (
            <CSVReader
              config={{
                skipEmptyLines: true,
              }}
              onUploadAccepted={onUploadAccepted}
              onDragOver={event => {
                event.preventDefault()
                setZoneHover(true)
              }}
              onDragLeave={event => {
                event.preventDefault()
                setZoneHover(false)
              }}
              onUploadRejected={(files, event) => {
                //console.log(files, event)
                setZoneHover(false)
              }}
              accept={"text/csv, .csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}
            >
              {({ getRootProps, acceptedFile, ProgressBar, getRemoveFileProps }) => {
                return (
                  <UploadZoneWrapper>
                    {message && (
                      <Alert severity="error" style={{ marginBottom: 8 }}>
                        <div dangerouslySetInnerHTML={{ __html: message }}></div>
                      </Alert>
                    )}

                    <UploadZone {...getRootProps()} style={zoneHover ? { borderColor: "#ffcb21" } : {}}>
                      {acceptedFile && !message ? (
                        <>
                          <Stack direction={"row"} spacing={2} style={{ justifyContent: "center", alignItems: "center" }}>
                            <Typography>Generating: {acceptedFile.name}</Typography>
                            <Tooltip placement="top" title="Cancel">
                              <IconButton
                                aria-label="close"
                                {...getRemoveFileProps()}
                                onClick={event => {
                                  getRemoveFileProps().onClick(event)
                                  //console.log("getRemoveFileProps")
                                  filedRemoved.current = true
                                }}
                              >
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <CircularProgress size={64} style={{ marginTop: 8 }} />
                        </>
                      ) : (
                        <Stack style={{ alignItems: "center" }}>
                          <Box component={"img"} src={Upload} style={{ width: "45%" }} />
                          <Typography>Click here to browse file or drop files here to upload</Typography>
                        </Stack>
                      )}
                    </UploadZone>

                    {!acceptedFile && (
                      <Stack spacing={2} direction={"row"} sx={{ mt: 2, justifyContent: "space-between" }}>
                        <Box style={{ alignSelf: "center" }}>
                          Download a
                          <Link href={downloadLink} color="info" underline="hover" style={{ paddingLeft: 4, paddingRight: 4 }}>
                            CSV template
                          </Link>
                          to see an example of the format required.
                        </Box>

                        <Link href={downloadLink}>
                          <Button variant="outlined" color="info" startIcon={<FileDownloadIcon />}>
                            Download Template
                          </Button>
                        </Link>
                      </Stack>
                    )}
                  </UploadZoneWrapper>
                )
              }}
            </CSVReader>
          )}
        </DialogContentWrapper>
        {data && !filedRemoved.current && (
          <DialogActions>
            {isSubmitting ? (
              <ProgressWrapper>
                <CircularProgress size={32} />
              </ProgressWrapper>
            ) : (
              <SpaceBetweenBoxWrapper>
                <Button id="btnDiscard" color="info" variant="outlined" onClick={handleDiscard} startIcon={<CloseIcon />}>
                  Discard
                </Button>

                <Tooltip placement="top" title={showErrorOnly ? "Click to show all rows" : "Click to show rows that contain errors only"}>
                  <Button
                    id="btnShowRows"
                    color={showErrorOnly ? "info" : "error"}
                    variant="outlined"
                    onClick={() => setShowErrorOnly(!showErrorOnly)}
                    startIcon={showErrorOnly ? <DoneAll /> : <ErrorOutline />}
                  >
                    {showErrorOnly ? "Show all rows" : "Show errors only"}
                  </Button>
                </Tooltip>

                <Button id="btnSubmit" variant="contained" disabled={!isDataValid()} onClick={handleSubmit} endIcon={<PublishIcon />}>
                  Submit
                </Button>
              </SpaceBetweenBoxWrapper>
            )}
          </DialogActions>
        )}
      </Dialog>
      <DisplayResultDialog
        message={alertContent}
        fileName={curFileName.current}
        title={title}
        isSuccess={wasSuccess}
        open={openAlert}
        onClose={handleCloseInDisplayResult}
        onConfirm={handleConfirmInDisplayResult}
      />
      <ImportConfirmDialog open={openConfirm} isDiscard={isDiscard} title={title} onClose={() => setOpenConfirm(false)} onConfirm={handleConfirmOrDiscardImport} />
    </>
  )
}

export default UploadDialog
